import React, { useEffect, useState } from 'react';
import { useApplyScopeLock, useLocationOptions } from '../Admin/shared/useLocationOptions';
import { Field, Select, INP, INP_LOCKED } from '../reusable/formRecords/formFieldPrimitives';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { registryCentralApi } from '../../api/registryCentralApi';
import { MARKET_SCHEDULE_OPTIONS, MARKET_STATUS_OPTIONS, MARKET_TYPE_OPTIONS } from './marketTradOptions';

const EMPTY = {
  state: '', lga: '', ward: '', name: '',
  market_type: MARKET_TYPE_OPTIONS[0]?.value ?? '',
  market_status: MARKET_STATUS_OPTIONS[0]?.value ?? '',
  market_schedule: MARKET_SCHEDULE_OPTIONS.find((o) => o.value === 'weekly')?.value ?? MARKET_SCHEDULE_OPTIONS[0]?.value ?? '',
};
const labelOf = (options, value) => options.find((o) => o.value === value)?.label ?? value;

// "+ Propose" for the Market lookup (e.g. on Market Price) — registry-first:
// the proposal goes to Registry Central as Pending Validation and R01 is
// notified; the notification opens the entry in Registry Central, where R01
// validates or rejects it. A validated market is completed in Market Registry
// (seeded with these details) before it appears in Market lookups.
// Location scope: R04 keeps their assigned state and picks any LGA / ward in
// it; R05 keeps their state and LGA and picks the ward where the market is.
export default function ProposeMarketModal({ isOpen, onClose, onCreated }) {
  const { user } = useAuth();
  const { pushNotification } = useNotifications();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { stateOptions, lgaOptions, wardOptions, loadingLgas, loadingWards } = useLocationOptions(f.state, f.lga);
  const { lockedState, lockedLga } = useApplyScopeLock(f, setF, { enabled: isOpen });

  // Reset on open, pre-filled with the user's assigned state (R04 / R05) and
  // LGA (R05) — a plain EMPTY reset here would wipe what the scope lock set.
  useEffect(() => {
    if (!isOpen) return;
    setF({ ...EMPTY, state: lockedState, lga: lockedLga });
    setError('');
    setBusy(false);
  }, [isOpen, lockedState, lockedLga]);

  if (!isOpen) return null;

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setF((prev) => (key === 'state' ? { ...prev, state: value, lga: '', ward: '' }
      : key === 'lga' ? { ...prev, lga: value, ward: '' } : { ...prev, [key]: value }));
  };
  const canCreate = f.name.trim().length >= 3 && f.state && f.lga && f.ward && f.market_type && f.market_status && f.market_schedule;

  const submit = async () => {
    if (!canCreate || busy) return;
    setBusy(true);
    setError('');
    try {
      const entry = await registryCentralApi.propose({ registry_type: 'market', ...f, name: f.name.trim() }, user);
      const where = [f.ward, f.lga, f.state].filter(Boolean).join(', ');
      pushNotification({
        type: 'registry_proposal',
        moduleKey: 'registry-central',
        moduleLabel: 'Registry Central',
        title: 'New Market Proposed',
        message: `${user?.name || 'A user'}${user?.roleId ? ` (${user.roleId})` : ''} proposed the market “${entry.name}” (${labelOf(MARKET_TYPE_OPTIONS, f.market_type)}) in ${where}. Open Registry Central to validate or reject it.`,
        route: `/registry-management/central?entry=${entry.uuid}`,
        recipientRoles: ['R01'],
        actorRoleId: user?.roleId,
      });
      onCreated?.(entry);
      onClose();
    } catch (err) {
      setError(err?.message ?? 'Failed to send the market proposal.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1250] flex items-center justify-center bg-black/50 px-4">
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Propose New Market</h3>
            <p className="mt-0.5 text-xs text-gray-400">Sent to Registry Central — R01 validates or rejects it</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-full text-xl leading-none text-gray-400 hover:bg-gray-100">×</button>
        </div>
        <div className="space-y-4 p-6">
          {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-700">{error}</div>}
          <div className="grid grid-cols-3 gap-4">
            <Field label="State" required hint={lockedState ? 'Your assigned state.' : undefined}>
              <Select value={f.state} onChange={set('state')} options={stateOptions} placeholder="Select state"
                disabled={Boolean(lockedState)} className={lockedState ? INP_LOCKED : INP} />
            </Field>
            <Field label="LGA" required hint={lockedLga ? 'Your assigned LGA.' : undefined}>
              <Select value={f.lga} onChange={set('lga')} options={lgaOptions} disabled={Boolean(lockedLga) || !f.state || loadingLgas}
                placeholder={f.state ? (loadingLgas ? 'Loading LGAs…' : 'Select LGA') : 'Select state first'}
                className={lockedLga || !f.state ? INP_LOCKED : INP} />
            </Field>
            <Field label="Ward" required>
              <Select value={f.ward} onChange={set('ward')} options={wardOptions} disabled={!f.lga || loadingWards}
                placeholder={f.lga ? (loadingWards ? 'Loading wards…' : 'Select ward') : 'Select LGA first'}
                className={f.lga ? INP : INP_LOCKED} />
            </Field>
          </div>
          <Field label="Market Name" required hint="Minimum 3 characters — the market's official name.">
            <input value={f.name} onChange={set('name')} placeholder="e.g. Sabon Gari Market" className={INP} />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Market Type" required>
              <Select value={f.market_type} onChange={set('market_type')} options={MARKET_TYPE_OPTIONS} placeholder="Select market type" className={INP} />
            </Field>
            <Field label="Status" required>
              <Select value={f.market_status} onChange={set('market_status')} options={MARKET_STATUS_OPTIONS} placeholder="Select status" className={INP} />
            </Field>
            <Field label="Market Schedule" required>
              <Select value={f.market_schedule} onChange={set('market_schedule')} options={MARKET_SCHEDULE_OPTIONS} placeholder="Select schedule" className={INP} />
            </Field>
          </div>
          <p className="text-xs text-gray-500">
            R01 is notified and validates or rejects the proposal in Registry Central. The market can be selected once it is validated and completed in Market Registry.
          </p>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="h-11 flex-1 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Close</button>
            <button type="button" onClick={submit} disabled={!canCreate || busy}
              className="h-11 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40">
              {busy ? 'Proposing…' : 'Propose'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
